import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Mail, 
  RotateCcw,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const VerificationPending = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { resendVerification } = useAuth();
  
  // Get user data from navigation state or props
  const user = location.state?.user;
  const userId = location.state?.userId;
  
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleResendVerification = async () => {
    const targetUserId = user?.id || userId;
    
    if (!targetUserId) {
      setError(t('auth.userIdNotAvailable'));
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      const result = await resendVerification(targetUserId);
      
      if (result.success) {
        setMessage(result.message);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t('auth.unexpectedError'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="glass-card">
            <div className="py-8">
              <div className={`text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                <Mail className="h-20 w-20 mx-auto mb-6" style={{ color: 'var(--accent-primary)' }} />
                <h1 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.checkYourEmail')}
                </h1>
                
                <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {user?.email ? (
                    t('auth.verificationLinkSent', { email: user.email })
                  ) : (
                    t('auth.checkEmailForActivation')
                  )}
                </p>

                {error && (
                  <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: 'var(--error-color)'
                  }}>
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}
                
                {message && (
                  <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                    background: 'rgba(34, 197, 94, 0.1)', 
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    color: 'var(--success-color)'
                  }}>
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{message}</span>
                  </div>
                )}

                <div className="border-t border-gray-300 my-6"></div>
                
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.didntReceiveEmail')}
                </h3>
                
                <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {t('auth.checkSpamFolder')}
                </p>
                
                <button
                  className="glass-button w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                >
                  {resendLoading ? (
                    <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t('auth.sending')}</span>
                    </div>
                  ) : (
                    <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <RotateCcw className="h-5 w-5" />
                      <span>{t('auth.resendVerificationEmail')}</span>
                    </div>
                  )}
                </button>
                
                <div className="space-y-3">
                  <button 
                    className="glass-button-outline w-full py-3 text-lg font-semibold"
                    onClick={() => navigate('/login')}
                  >
                    <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <ArrowLeft className="h-5 w-5" />
                      <span>{t('auth.backToLogin')}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPending;
